import { useCallback, useEffect, useRef, useState } from "react";
import {
  type BookmarkLinkAnswer,
  type BookmarkLinkRefusal,
  requestBookmarkLink,
} from "./bookmark-link";

export type BookmarkLinkState =
  | { status: "loading" }
  | { status: "refused"; reason: BookmarkLinkRefusal }
  | {
      status: "ready";
      url: string;
      /** False when the reader's existing link came back unchanged. */
      issued: boolean;
      resetting: boolean;
      /** Why the last reset left the old link standing, until the next one. */
      resetRefusal: BookmarkLinkRefusal | null;
    };

export interface BookmarkLinkDeps {
  request: (
    body: { reset?: boolean },
    signal?: AbortSignal,
  ) => Promise<BookmarkLinkAnswer>;
}

export const browserBookmarkLinkDeps: BookmarkLinkDeps = {
  request: (body, signal) => requestBookmarkLink(body, undefined, signal),
};

/** Null when nothing was asked: no link yet, a reset in flight, or unmounted. */
export type ResetOutcome = "reset" | BookmarkLinkRefusal | null;

export interface BookmarkLink {
  state: BookmarkLinkState;
  /** Revokes the link and issues a new one; a no-op while one is in flight. */
  reset: () => Promise<ResetOutcome>;
  /** Asks again after a refusal. */
  retry: () => void;
}

const LOADING: BookmarkLinkState = { status: "loading" };

function readyFrom(answer: Extract<BookmarkLinkAnswer, { kind: "link" }>) {
  return {
    status: "ready" as const,
    url: answer.url,
    issued: answer.issued,
    resetting: false,
    resetRefusal: null,
  };
}

/**
 * Fetches the reader's bookmark link on mount, so a dialog that mounts
 * this only while open asks again each time it opens: that returns the
 * same link and costs nothing against the rate limit, and a link revoked
 * elsewhere is never shown stale.
 */
export function useBookmarkLink(
  deps: BookmarkLinkDeps = browserBookmarkLinkDeps,
): BookmarkLink {
  const [state, setState] = useState<BookmarkLinkState>(LOADING);
  const [attempt, setAttempt] = useState(0);
  const mounted = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    mounted.current = controller;
    void deps.request({}, controller.signal).then((answer) => {
      if (controller.signal.aborted) return;
      setState(
        answer.kind === "link"
          ? readyFrom(answer)
          : { status: "refused", reason: answer.reason },
      );
    });
    return () => {
      controller.abort();
      mounted.current = null;
    };
  }, [deps, attempt]);

  const retry = useCallback(() => {
    setState(LOADING);
    setAttempt((n) => n + 1);
  }, []);

  const reset = useCallback((): Promise<ResetOutcome> => {
    const controller = mounted.current;
    if (!controller || state.status !== "ready" || state.resetting)
      return Promise.resolve(null);
    setState({ ...state, resetting: true, resetRefusal: null });
    return deps.request({ reset: true }, controller.signal).then((answer) => {
      if (controller.signal.aborted) return null;
      setState((current) =>
        current.status !== "ready"
          ? current
          : answer.kind === "link"
            ? readyFrom(answer)
            : { ...current, resetting: false, resetRefusal: answer.reason },
      );
      return answer.kind === "link" ? "reset" : answer.reason;
    });
  }, [state, deps]);

  return { state, reset, retry };
}
